import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { DatabaseInconsistencyLog, OrganizationDatabaseInconsistencyLogStore } from 'src/stores/organizations-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Card, Row } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import Moment from 'moment';
import { useIdentity } from 'src/core/services/authentication';
import { ENV } from 'src/';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AppConfiguration } from 'src/core/services/configuration.service';
import i18next from 'i18next';
import { PermissionsConstants } from 'src/common/types';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';

const permissions = [PermissionsConstants.ManageAdminOrganization];

const OrganizationDbInconsistencyLogs: FC = () => {
  const identity = useIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const config: AppConfiguration = useContext(AppConfig);
  const { t } = useTranslation();
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);

  const hasManageAdminOrganitionsPermission = !checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManageAdminOrganization);

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'CreatedOn', direction: 'Descending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const databaseLogsStore = container.get(OrganizationDatabaseInconsistencyLogStore);
  const databaseLogsState = databaseLogsStore.state;

  setHeader(t('DatabaseCheckLogs'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'organizations', breadcrumbName: t('Organization') },
    { path: 'logs', breadcrumbName: t('OrganizationLogs') },
  ]);

  useEffect(
    () => {
      if (!hasManageAdminOrganitionsPermission) return;
      databaseLogsStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (!hasManageAdminOrganitionsPermission) {
    navigate('/organizations');
    return null;
  }

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        filter: <DateRangeFilter />,
        fiterField: 'CreatedOn',
        field: 'CreatedOn',
        title: t('CreatedOn'),
        width: '20%',
        renderer: (data) => {
          const formattedDate = Moment.utc(data.createdOn).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`);
          return (
            <span>
              <Link to={{ pathname: `inconsistency-logs/${data.id}` }} state={{ ...location.state, date: formattedDate }}>
                {formattedDate}
              </Link>
            </span>
          );
        },
      },
      {
        sortable: true,
        searchable: true,
        field: 'organizationName',
        title: t('OrganizationName'),
        width: '25%',
        renderer: (data) => <span>{data.organizationName}</span>,
      },
      {
        sortable: true,
        searchable: true,
        sortField: 'CreatedBy',
        field: 'CreatedBy',
        title: t('CreatedBy'),
        width: '30%',
        renderer: (data) => <span>{data.userName ? t(data.userName) : ''}</span>,
      },
      {
        field: 'Items',
        title: t('Items'),
        renderer: (data) => <span>{data.items}</span>,
      },
    ],
    data: { ...databaseLogsState.value, isBusy: databaseLogsState.value.isBusy },
  } as TableModel<DatabaseInconsistencyLog>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew={false}
          canDelete={false}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            databaseLogsStore.load(query);
          }}
          onRefresh={() => databaseLogsStore.load(query)}
          model={databaseLogsState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            databaseLogsState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(databaseLogsState.errorMessage.value) : t('DatabaseLogsTableError'))
          }
        />
      </Card>
    </Row>
  );
};

export default OrganizationDbInconsistencyLogs;
